import { useEffect, useRef } from 'react';
import type { ViewMode } from '../types';
import { IS_WEB } from './platform';

/**
 * Web uygulamasında ekranların adresleri. Masaüstünde adres çubuğu yok,
 * ekranlar yalnızca durumla (App > currentView) değişiyor; web'de geri tuşu,
 * sayfa yenileme ve toplantı bağlantısı paylaşma için durum adresle eşleniyor:
 *
 *   /                   ana sayfa          /kisiler, /kisiler/<id>
 *   /toplanti/<id>      not sayfası        /sirketler, /sirketler/<id>
 *   /gunluk             günlük rapor       /baglantilar, /cop-kutusu
 *   /sohbet             AI sohbet          /ayarlar, /yonetim
 *   /takvim             takvim (telefon)   /menu (telefon: Daha fazla)
 *
 * Kayıt ekranının adresi yok: yenilenince kayıt biter (parçalar kurtarılır),
 * o adrese dönmenin anlamı olmaz.
 */

export interface LocationState {
  view: ViewMode;
  meetingId: string | null;
  personId: string | null;
  companyId: string | null;
  folderId: string | null;
}

const ID = /^[A-Za-z0-9-]{1,64}$/;

const SIMPLE: Partial<Record<ViewMode, string>> = {
  home: '/',
  'daily-digest': '/gunluk',
  chat: '/sohbet',
  people: '/kisiler',
  companies: '/sirketler',
  integrations: '/baglantilar',
  trash: '/cop-kutusu',
  settings: '/yonetim',
  preferences: '/ayarlar',
  calendar: '/takvim',
  more: '/menu',
};

export function pathFor(state: LocationState): string | null {
  if (state.view === 'recording') return null;
  if (state.view === 'meeting') return state.meetingId ? `/toplanti/${encodeURIComponent(state.meetingId)}` : '/';
  if (state.view === 'folder') return state.folderId ? `/klasor/${encodeURIComponent(state.folderId)}` : '/';
  if (state.view === 'person') return state.personId ? `/kisiler/${encodeURIComponent(state.personId)}` : '/kisiler';
  if (state.view === 'company') return state.companyId ? `/sirketler/${encodeURIComponent(state.companyId)}` : '/sirketler';
  return SIMPLE[state.view] ?? '/';
}

export function parsePath(pathname: string): LocationState {
  const base: LocationState = { view: 'home', meetingId: null, personId: null, companyId: null, folderId: null };
  const [first, second] = pathname.replace(/\/+$/, '').split('/').filter(Boolean).map((part) => decodeURIComponent(part));
  if (!first) return base;
  if (first === 'toplanti' && second && ID.test(second)) return { ...base, view: 'meeting', meetingId: second };
  if (first === 'klasor' && second && ID.test(second)) return { ...base, view: 'folder', folderId: second };
  if (first === 'kisiler') return second && ID.test(second) ? { ...base, view: 'person', personId: second } : { ...base, view: 'people' };
  if (first === 'sirketler') return second && ID.test(second) ? { ...base, view: 'company', companyId: second } : { ...base, view: 'companies' };
  const simple = (Object.entries(SIMPLE) as Array<[ViewMode, string]>).find(([, path]) => path === `/${first}`);
  return simple ? { ...base, view: simple[0] } : base;
}

/**
 * Durumu adrese, adresi duruma eşler (yalnızca web). `enabled` oturum açılıp
 * kurulum bitince true olur; ilk açılışta adresteki ekrana gidilir.
 */
export function useWebLocation(state: LocationState, apply: (next: LocationState) => void, enabled: boolean): void {
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const startedRef = useRef(false);
  /** Açılışta adresteki ekran uygulanana kadar durumun (henüz eski) adresi yazılmasın. */
  const pendingRef = useRef<string | null>(null);

  // Açılış: adresteki ekran (ör. paylaşılan toplantı bağlantısı).
  useEffect(() => {
    if (!IS_WEB || !enabled || startedRef.current) return;
    startedRef.current = true;
    const initial = parsePath(window.location.pathname);
    if (initial.view === 'home') {
      // Tanınmayan adres ana sayfaya — geçmişe kayıt eklemeden.
      if (window.location.pathname !== '/') window.history.replaceState(null, '', `/${window.location.hash}`);
      return;
    }
    pendingRef.current = pathFor(initial);
    applyRef.current(initial);
  }, [enabled]);

  // Geri / ileri.
  useEffect(() => {
    if (!IS_WEB || !enabled) return;
    const onPop = () => applyRef.current(parsePath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [enabled]);

  // Durum değişti → adres. İlk eşleme geçmişe yeni kayıt eklemez.
  const path = pathFor(state);
  useEffect(() => {
    if (!IS_WEB || !enabled || !startedRef.current || path === null) return;
    if (pendingRef.current !== null) {
      if (path === pendingRef.current) pendingRef.current = null;
      return;
    }
    if (window.location.pathname === path) return;
    window.history.pushState(null, '', `${path}${window.location.hash}`);
  }, [enabled, path]);
}
